import { loadGoogleMapsScript } from '../../utils/googleMapsLoader';
import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { Button } from "../../components/form/Button";
import CrossIcon from "../../assets/icons/CrossIcon";
import DocumentIcon from "../../assets/icons/document-text";
import AddIcon from "../../assets/icons/AddIcon";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import FormikSelect from "../../components/Formik/FormikSelect";
import { useTheme } from "../../middleware/ThemeContext";
import { useGetBulkUpdateFieldsQuery, useBulkUpdateFieldsMutation } from "../../api/searchQueryApiSlice";
import CustomTextArea from "../../components/form/CustomTextArea";
import AMSSelect from "../../components/AMSSelect";
import { CheckboxItem } from "../../components/listing/ColumnIdentifier";
import MultiSelectDropdown from "../../components/form/MultiSelectDropdown";
import CustomInput from "../../components/form/CustomInput";
import { useToast } from "../../components/toast/ToastContext";
import CalendarIcon from "../../assets/icons/CalendarIcon";

import ModalHeaderBg from "../../assets/images/CreateFormBackground.png";
import { font } from "../../utils/themes";
import StyledText from "../../components/StyledComponents/StyledText";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import SaveIcon from "../../assets/icons/SaveIcon";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import { LayoutWidget } from "../contacts/ContactOverview/DND/dndTypes";
import FieldControl from '../../components/FormBuilder/FieldPreviews/FieldControl';
interface BulkUpdateModalProps {
    show: boolean;
    onClose: () => void;
    scope: string;
    recordIds: number[];
    refetch?: () => void;
    prefillData?: LayoutWidget
}

interface FieldUpdate {
    id: string;
    field: string;
    value: any;
}


interface FormValues {
    updates: FieldUpdate[];
}

const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({
    show,
    onClose,
    scope = "contact",
    recordIds = [],
    refetch,
    prefillData
}) => {

    const showToast = useToast();
    const { theme } = useTheme();
    const { data: bulkFields = [], isLoading } = useGetBulkUpdateFieldsQuery(scope);
    const [updateFields, { isLoading: isUpdating }] = useBulkUpdateFieldsMutation();
    const initialValues: FormValues = useMemo(() => {
        const updates: FieldUpdate[] = [];

        if (prefillData?.widget?.fields) {
            for (const wrapper of prefillData.widget.fields) {
                const f = wrapper.field;

                if (!f || !f.accessKey || !f.dataType || f.accessKey === null) {
                    continue;
                }

                let processedValue: any;

                // Handle null/undefined first
                if (f.value == null || f.value === "") {
                    processedValue = null;

                    // But for non-nullable fields like text, use empty string
                    if (!["NUMBER", "DECIMAL", "CURRENCY", "DATE", "DATE_TIME", "BOOLEAN", "CHECKBOX"].includes(f.dataType)) {
                        processedValue = "";
                    }
                } else {
                    const rawValue = f.value as any;

                    // f.value is not null/undefined
                    if (f.dataType === "MULTIPLE_CHOICES") {
                        if (rawValue == null || rawValue === "") {
                            processedValue = [];
                        } else if (Array.isArray(rawValue)) {
                            processedValue = rawValue;
                        } else if (typeof rawValue === "string") {
                            processedValue = rawValue.split(",").map(s => s.trim()).filter(s => s !== "");
                        } else {
                            processedValue = [];
                        }
                    }
                    else if (["BOOLEAN", "CHECKBOX"].includes(f.dataType)) {
                        processedValue = f.value === true || f.value === "true" || f.value === 1 || f.value === "1";
                    }
                    else if (f.dataType === "NUMBER") {
                        const num = Number(f.value);
                        processedValue = isNaN(num) ? null : num;
                    }
                    else if (["DECIMAL", "CURRENCY"].includes(f.dataType)) {
                        const num = parseFloat(String(f.value));
                        processedValue = isNaN(num) ? null : num;
                    }
                    else if (["DATE", "DATE_TIME"].includes(f.dataType)) {
                        processedValue = f.value; // assume backend sends valid string
                    }
                    else if (["FILE_UPLOAD", "IMAGE_UPLOAD"].includes(f.dataType)) {
                        processedValue = f.value;
                    }
                    else {
                        // SINGLE_LINE, MULTI_LINE, SELECT, RADIO, etc.
                        processedValue = String(f.value);
                    }
                }
                // else: keep as-is (string, etc.)

                updates.push({
                    id: `prefill-${f.accessKey}`,
                    field: f.accessKey,
                    value: processedValue, // ✅ ab TypeScript ko pata hai ke value: any hai
                });
            }
        }

        return {
            updates: updates.length > 0 ? updates : [{ id: Date.now().toString(), field: "", value: "" }]
        };
    }, [prefillData]);

    const formatValueForPayload = (value: any): string => {
        if (Array.isArray(value)) {
            const normalized = value
                .map((item) => {
                    if (item == null) return '';
                    if (typeof item === 'object') {
                        const candidate = (item as any).label ?? (item as any).value ?? (item as any).name;
                        return candidate !== undefined ? String(candidate) : JSON.stringify(item);
                    }
                    return String(item);
                })
                .filter(Boolean);
            return normalized.join(', ');
        }

        if (value && typeof value === 'object') {
            return JSON.stringify(value);
        }

        if (value === undefined || value === null) return '';

        return value.toString();
    };

    const handleSubmit = async (values: typeof initialValues) => {

        try {
            const payload = {
                fields: values.updates.map(({ field, value }) => ({
                    fieldAccessKey: field,
                    value: formatValueForPayload(value),
                })),
                scope,
                recordIds,
            };
            await updateFields(payload).unwrap();
            showToast('Bulk Update Successful', 'Fields updated successfully.', 'success');
            refetch && refetch();
            onClose();
        } catch (error) {
            showToast('Bulk Update Failed', 'Failed to update fields. Please try again.', 'error');
        }
    };

    const renderValueField = (
        fieldKey: string,
        index: number,
        setFieldValue: (field: string, value: any) => void,
        currentValue: any,
        error: string | undefined
    ) => {
        const selectedField = bulkFields.find((field: any) => field.accessKey === fieldKey);

        if (!selectedField) return null;
        return (
            <FieldControl
                key={fieldKey}
                field={selectedField}
                isRequired={selectedField ? true : false}
                value={currentValue}
                onChange={(value: any) => setFieldValue(`updates.${index}.value`, value)}
                error={error}
            />);
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <StyledModalContent theme={theme}>
                <ModalHeader>
                    <ModalTitle>
                        <StyledText textColor={theme.CorporateTheme?.colors?.primary} fontWeight={600} fontSize="20px" fontFamily={font.raleway}>Bulk Update</StyledText>
                        <StyledText
                            fontSize="14px"
                            fontWeight={500}
                            textColor={theme.CorporateTheme?.colors?.textSecondary}
                            lineHeight="1.5"
                            className="mt-2"
                        >
                            Enables users to update to several selected records at once<br /> via the bulk action option.
                        </StyledText>
                    </ModalTitle>
                </ModalHeader>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <Form>
                            <ModalBody >
                                <div className="d-flex flex-column px-4 py-4  gap-2 border rounded mx-5">
                                    <FieldArray name="updates">
                                        {({ push, remove }) => (
                                            <>
                                                {values.updates.map((update, index) => {
                                                    const selectedField = bulkFields.find(
                                                        (field: any) => field.accessKey === update.field
                                                    );
                                                    const valueError = (errors.updates as any)?.[index]?.value;
                                                    const valueTouched = (touched.updates as any)?.[index]?.value;

                                                    return (
                                                        <div key={update.id} className="d-flex flex-column gap-2">
                                                            <StyledText textColor={theme.CorporateTheme?.colors?.headerColor} fontWeight={600} fontSize="14px" fontFamily={font.raleway}>Select a field</StyledText>

                                                            <FieldRow className=" d-flex align-items-center mb-2  gap-2">

                                                                <div className="d-flex gap-2 w-100">
                                                                    <FieldWrapper style={{ width: "50%" }}>
                                                                        <AMSSelect
                                                                            staticOptions={bulkFields.map((field: any) => ({
                                                                                label: field.label,
                                                                                value: field.accessKey,
                                                                                accessKey: field.accessKey,
                                                                                dataType: field.dataType,
                                                                            }))}
                                                                            showDataType
                                                                            includeAccessKey
                                                                            // showIcons
                                                                            // includeId={false}
                                                                            value={
                                                                                values.updates[index].field
                                                                                    ? {
                                                                                        label:
                                                                                            bulkFields.find((f: any) => String(f.accessKey) === String(values.updates[index].field))
                                                                                                ?.label ??
                                                                                            String(values.updates[index].field),
                                                                                        value: values.updates[index].field,
                                                                                    }
                                                                                    : null
                                                                            }
                                                                            onChange={(opt: any) => {
                                                                                const fieldValue = opt?.value ? String(opt.value) : "";
                                                                                setFieldValue(`updates.${index}.field`, fieldValue);
                                                                                setFieldValue(`updates.${index}.value`, "");
                                                                            }}
                                                                            placeholder="Select field here"
                                                                            menuPosition="fixed"
                                                                        />

                                                                    </FieldWrapper>
                                                                    <FieldWrapper style={{ width: "50%" }}>
                                                                        {selectedField
                                                                            ? renderValueField(
                                                                                selectedField.accessKey,
                                                                                index,
                                                                                setFieldValue,
                                                                                update.value,
                                                                                valueError && valueTouched ? valueError : undefined
                                                                            )
                                                                            : (
                                                                                <FieldContainer>
                                                                                    <CustomInput
                                                                                        placeholder="Select a field first"
                                                                                        value="Select a field first"
                                                                                        onChange={() => { }}
                                                                                        disabled
                                                                                    />
                                                                                </FieldContainer>
                                                                            )}
                                                                    </FieldWrapper>
                                                                </div>
                                                                <IconWidth>
                                                                    {index > 0 ? (
                                                                        <DeleteIcon size={20} onClick={() => remove(index)} className="cursor-pointer" color={theme.CorporateTheme.colors.danger} />
                                                                    ) : null}
                                                                </IconWidth>
                                                            </FieldRow>
                                                        </div>
                                                    );
                                                })}

                                                {typeof errors.updates === 'string' ? (
                                                    <ErrorText style={{ marginBottom: '16px' }}>{errors.updates}</ErrorText>
                                                ) : null}

                                                <Button
                                                    theme={theme}
                                                    type="button"
                                                    className="cursor-pointer mt-2"
                                                    $variant="customButton"
                                                    btnBg="transparent"
                                                    borderColor={theme.CorporateTheme.colors.primary}
                                                    color={theme.CorporateTheme.colors.primary}
                                                    width="178px"
                                                    onClick={() =>
                                                        push({
                                                            id: Date.now().toString(),
                                                            field: "",
                                                            value: "",
                                                        })
                                                    }
                                                >
                                                    <AddIcon color={theme.CorporateTheme.colors.primary} />
                                                    Add another field
                                                </Button>
                                            </>
                                        )}
                                    </FieldArray>
                                </div>
                                <div className="d-flex align-items-center justify-content-end mt-4 me-5">
                                    <div className="d-flex gap-1">
                                        <Button
                                            theme={theme}
                                            type="button"
                                            className="cursor-pointer"
                                            $variant="cancel"
                                            onClick={onClose}
                                            disabled={isUpdating}
                                        >
                                            <CrossIcon />
                                            Cancel
                                        </Button>
                                        <Button
                                            theme={theme}
                                            type="submit"
                                            $variant="add"
                                            className="cursor-pointer"
                                            disabled={
                                                isUpdating ||
                                                !values.updates.some((u) => String(u.field || '').trim().length > 0)
                                            }
                                        >
                                            <div className="d-flex align-items-center gap-1">
                                                {isUpdating ? (
                                                    <>
                                                        <LoadingCircleIcon color={theme.CorporateTheme.colors.background} />
                                                        <span>Save</span>
                                                        {/* Add a loading spinner if available */}
                                                    </>
                                                ) : (
                                                    <>
                                                        <SaveIcon color={theme.CorporateTheme.colors.background} />
                                                        <label className="cursor-pointer">Save</label>
                                                    </>
                                                )}
                                            </div>
                                        </Button>
                                    </div>
                                </div>
                            </ModalBody>
                        </Form>
                    )}
                </Formik>
                <ModalFooter></ModalFooter>
            </StyledModalContent>
        </Modal>
    );
};

export default BulkUpdateModal;

// Styled components
const StyledModalContent = styled.div`
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  background: ${({ theme }) => theme.colors.contentBackground};
`;

const ModalHeader = styled.div`
  position: relative;
  padding-bottom: 30px;
  background-image: url(${ModalHeaderBg});
  background-size: cover;
  background-position: center;

`;

const ModalTitle = styled.div`
  text-align: center;
  padding: 0 30px;
  margin-top: 30px;
`;

const IconWidth = styled.div`
  width: 10px;
`;

const ModalBody = styled.div`
  padding-top: 0;
`;

const ModalCloseButton = styled.button`
  position: absolute;
  bottom: 50px;
  right: 10px;
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    color: #333;
    padding: 0px;
  }
`;

const TitleText = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.CorporateTheme?.colors.primary};
`;

// const StyledText = styled.div<{
//     fontSize?: string;
//     fontWeight?: number;
//     textColor?: string;
// }>`
//   font-size: ${(props) => props.fontSize || "14px"};
//   font-weight: ${(props) => props.fontWeight || 400};
//   color: ${(props) => props.textColor || "inherit"};
// `;

const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  padding: 30px;
`;

const FieldRow = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
`;

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
//   min-height: 72px; /* Fixed height to prevent layout shift */
`;

const FieldContainer = styled.div`
  position: relative;
  min-height: 42px; /* Fixed height for input fields */
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 16px;
  margin-top: 4px;
  position: absolute;
  bottom: -20px;
  left: 0;
  right: 0;
  min-height: 16px;
`;

const RemoveButton = styled.button<{ theme: any }>`
  background: none;
  border: none;
  color: ${({ theme }) => theme.CorporateTheme?.colors?.textSecondary};
  cursor: pointer;
  font-size: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: -30px;
  top: 25%;
  transform: translateY(-50%);

  &:hover {
    color: ${({ theme }) => theme.CorporateTheme?.colors?.primary};
  }
`;


interface AddressAutocompleteBUProps {
    value: string | { formattedAddress?: string; value?: string };
    onChange: (value: any) => void;
    placeholder?: string;
    disabled?: boolean;
    mode?: 'address-line' | 'city' | 'state' | 'postal-code';
}

const AutocompleteInput = styled.input<{ theme: any }>`
    padding: 10px 12px;
    border-radius: 5px;
    border: 1px solid ${({ theme }) => theme.CreateFormTheme?.colors?.inputBorders};
    background-color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputBackground};
    color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputColor};
    width: 100%;

    &::placeholder {
        color: ${({ theme }) => theme.CreateFormTheme?.colors?.placeHolder};
    }

    &:disabled {
        background-color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputBackground};
        cursor: not-allowed;
        color: ${({ theme }) => theme.CreateFormTheme?.colors?.placeHolder};
    }

    &:focus {
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        outline: none;
    }
`;

const AddressAutocompleteBU: React.FC<AddressAutocompleteBUProps> = ({
    value,
    onChange,
    placeholder,
    disabled,
    mode = 'address-line',
}) => {
    const { theme } = useTheme();
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const autocompleteRef = React.useRef<any>(null);
    const [initError, setInitError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        loadGoogleMapsScript()
            .then(() => {
                if (cancelled) return;
                const google: any = (window as any).google;
                if (!google?.maps?.places || !inputRef.current) {
                    setInitError('Google Maps Places library not available');
                    return;
                }
                setInitError(null);
                const options = {
                    fields: [
                        'address_components',
                        'formatted_address',
                        'geometry',
                        'place_id',
                        'name',
                    ],
                };
                autocompleteRef.current = new google.maps.places.Autocomplete(
                    inputRef.current,
                    options
                );
                autocompleteRef.current.addListener('place_changed', () => {
                    const place = autocompleteRef.current.getPlace();
                    const formatted = place?.formatted_address || inputRef.current?.value || '';
                    const components: Record<string, string> = {};
                    (place?.address_components || []).forEach((c: any) => {
                        const type = c.types?.[0];
                        if (type) components[type] = c.long_name;
                    });
                    let derived: any = formatted;
                    if (mode === 'city') {
                        derived = components.locality || components.sublocality || components.administrative_area_level_2 || '';
                    } else if (mode === 'state') {
                        derived = components.administrative_area_level_1 || '';
                    } else if (mode === 'postal-code') {
                        derived = components.postal_code || '';
                    } else if (mode === 'address-line') {
                        const street = [components.street_number, components.route].filter(Boolean).join(' ');
                        derived = street || formatted;
                    }
                    onChange(derived || formatted || '');
                });
            })
            .catch(() => {
                setInitError('Failed to load Google Maps script');
            });
        return () => {
            cancelled = true;
        };
    }, [mode]);

    const displayValue = typeof value === 'string' ? value : (value as any)?.value || (value as any)?.formattedAddress || '';

    return (
        <div style={{ position: 'relative' }}>
            <AutocompleteInput
                theme={theme}
                type="text"
                placeholder={placeholder || 'Enter address'}
                value={displayValue}
                ref={inputRef as any}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                disabled={disabled}
                autoComplete="off"
            />
            {/* Debug overlay for troubleshooting */}
            {initError && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: '#fffbe6', color: '#ad6800', fontSize: 12, zIndex: 10, padding: 4, border: '1px solid #ffe58f', borderRadius: 4 }}>
                    Autocomplete not active: {initError}
                </div>
            )}
        </div>
    );
};